import { Check, Circle } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

type AnalysisProgressProps = {
	isUploading: boolean;
};

const ANALYSIS_STEPS = [
	{ id: "upload", label: "Uploading document" },
	{ id: "extract", label: "Extracting information" },
	{ id: "validate", label: "Running validation checks" },
	{ id: "finalize", label: "Finalizing analysis" }
];

const STEP_DELAY = 1000;

const AnalysisProgress = ({ isUploading }: AnalysisProgressProps) => {
	const [currentStep, setCurrentStep] = useState(0);

	useEffect(() => {
		let interval: NodeJS.Timeout | undefined;

		if (isUploading) {
			setCurrentStep(0);

			interval = setInterval(() => {
				setCurrentStep((prev) => {
					if (prev < ANALYSIS_STEPS.length - 1) {
						return prev + 1;
					}
					return prev;
				});
			}, STEP_DELAY);
		} else {
			setCurrentStep(ANALYSIS_STEPS.length);
		}

		return () => {
			if (interval) clearInterval(interval);
		};
	}, [isUploading]);

	return (
		<div className="flex h-full w-full flex-col items-center justify-center gap-6 p-6">
			<div className="flex w-full flex-col gap-4">
				<p className="text-sm font-semibold text-indigo-600">Analysis in Progress</p>
				<div className="flex flex-col gap-3">
					{ANALYSIS_STEPS.map((step, index) => {
						const isCompleted = index < currentStep;
						const isActive = index === currentStep;

						return (
							<div
								key={step.id}
								className={`flex items-center gap-3 transition-all duration-700 ease-in-out ${isCompleted
										? "opacity-100"
										: isActive
											? "opacity-100"
											: "opacity-50"
									}`}
								style={{
									transitionDelay: isCompleted ? `${index * 150}ms` : "0ms"
								}}>
								{isCompleted ? (
									<Check
										size={18}
										weight="bold"
										className="min-w-[18px] text-green-600 transition-all duration-500"
									/>
								) : isActive ? (
									<Circle
										size={18}
										weight="fill"
										className="min-w-[18px] animate-pulse text-indigo-600"
									/>
								) : (
									<Circle size={18} weight="fill" className="min-w-[18px] text-gray-400" />
								)}
								<span
									className={`text-[13px] transition-colors duration-500 ${isCompleted
											? "font-medium text-gray-600"
											: isActive
												? "font-semibold text-gray-900"
												: "font-normal text-gray-400"
										}`}>
									{step.label}
								</span>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default AnalysisProgress;
